/**
 * Adapter: traduce el state interno de los formularios de Clinical Notes
 * (que usa los componentes compartidos VitalsForm/HpiForm/Phq2Form/
 * AssessmentsPlanFollowUp/LocationForm — todos con keys PascalCase tipo
 * AdvancedMD field-names) al shape camelCase que espera buildEhrNotePayload.
 *
 * `exam` y `ros` no requieren traducción: sus keys ya coinciden con lo que
 * buildEhrNotePayload lee.
 *
 * Uso:
 *   const args = adaptFormStateToBuilder({
 *     patient, meta, location, reason, vitals, hpi, meds, pmh, ros, exam,
 *     phq2, assessPlanFu, chiefComplaint,
 *   });
 *   const payload = buildEhrNotePayload(args);
 *
 * @param {Object} input - State tal cual lo guardan los componentes compartidos.
 * @param {Object} input.patient - { patientId, profileId }
 * @param {Object} input.meta - { templateName, comment, noteDatetime, msgtime, nocookie }
 * @param {Object} input.location - { location, location1, location2 }
 * @param {Object} input.reason - { ReasonforAppointment } (LocationForm shape)
 * @param {Object} input.vitals - keys Vitals_Weight/Vitals_Height/... (VitalsForm shape)
 * @param {Object} input.hpi - { HistoryofPresentIllness, HistoryofPresentIllness1 } (HpiForm shape)
 * @param {Object} input.meds - { Allmedicationsreviewedandreconciled, CurrentMedications }
 * @param {Object} input.pmh - { PMH (bool), PMH2 (string) }
 * @param {Object} input.ros - keys ya alineadas con buildEhrNotePayload
 * @param {Object} input.exam - { [section]: { on, text } } — ya alineado
 * @param {Object} input.phq2 - { PHQ9_1_1, PHQ9_2_1, PHQ9scorereviewed }
 * @param {Object} input.assessPlanFu - { Assessments, Treatment, FollowUp }
 * @param {Object} [input.chiefComplaint] - { ChiefComplaints } — si viene, se ruta a hpi.general
 *   (el builder no acepta chiefComplaint como campo independiente; lo prepende
 *   al HPI general para que no se pierda).
 *
 * @returns {Object} args en el shape que buildEhrNotePayload consume.
 */
export default function adaptFormStateToBuilder({
  patient,
  meta,
  location,
  reason = {},
  vitals = {},
  hpi = {},
  meds = {},
  pmh = {},
  ros = {},
  exam = {},
  phq2 = {},
  assessPlanFu = {},
  chiefComplaint,
}) {
  const cc = chiefComplaint?.ChiefComplaints?.trim();
  const hpiGeneral = hpi.HistoryofPresentIllness || "";
  const general = cc && hpiGeneral
    ? `${cc}\n\n${hpiGeneral}`
    : cc || hpiGeneral;

  return {
    patient,
    meta,
    location,
    reason: {
      reasonForAppointment: reason.ReasonforAppointment || "",
    },
    vitals: {
      weight: vitals.Vitals_Weight ?? "",
      height: vitals.Vitals_Height ?? "",
      bmi: vitals.Vitals_BMI ?? "",
      temperature: vitals.Vitals_Temperature ?? "",
      pulse: vitals.Vitals_Pulse ?? "",
      o2SatRa: vitals.Vitals_O2SatRA ?? "",
      bpPrevSys: vitals.Vitals_BP_SystolicPrev ?? "",
      bpPrevDia: vitals.Vitals_BP_DiastolicPrev ?? "",
      bp1Sys: vitals.Vitals_BP_Systolic1 ?? "",
      bp1Dia: vitals.Vitals_BP_Diastolic1 ?? "",
      bpOption1: vitals.Vitals_BP_Option1 ?? "",
      bpLocation1: vitals.Vitals_BP_Location1 ?? "",
    },
    hpi: {
      general,
      tcm: hpi.HistoryofPresentIllness1 || "",
    },
    meds: {
      allReviewedAndReconciled: !!meds.Allmedicationsreviewedandreconciled,
      text: meds.CurrentMedications || "",
    },
    pmh: {
      noChangeReviewed: !!pmh.PMH,
      text: pmh.PMH2 || "",
    },
    ros,
    exam,
    phq2: {
      q1: phq2.PHQ9_1_1 ?? "",
      q2: phq2.PHQ9_2_1 ?? "",
      reviewedPhq9: phq2.PHQ9scorereviewed ?? "",
    },
    assessPlanFu: {
      assessments: assessPlanFu.Assessments || "",
      treatment: assessPlanFu.Treatment || "",
      followUp: assessPlanFu.FollowUp || "",
    },
  };
}
